<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
interface LoginData {
  username: string
  password: string
  remember: boolean
}
// Using generic error handling for different API error structures
import { getRegisterPermalink, getPasswordRecoveryPermalink } from '~/lib'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const emits = defineEmits<{
  (e: 'success'): void
  (e: 'close'): void
}>()

const { isLoggedIn, login } = useUser()
const { refreshSessionContext } = useSessionContext()
const { pushError } = useNotifications()
const localePath = useLocalePath()

type LoginError = {
  message?: string
  messages?: Array<{ detail: string }>
  data?: {
    message?: string
    errors?: Record<string, string[]>
  }
}

const isLoginError = (error: unknown): error is LoginError => typeof error === 'object' && error !== null

const invokeLogin = async (data: LoginData): Promise<void> => {
  try {
    data.remember = true
    await refreshSessionContext()
    await login(data)
    emits('success')
    emits('close')
  } catch (error) {
    console.error('Login error:', error)
    const e = isLoginError(error) ? error : {}

    // Handle different error structures
    if (e.messages && Array.isArray(e.messages)) {
      e.messages.forEach(({ detail }) => pushError(detail))
    } else if (e.message) {
      pushError(e.message)
    } else if (e.data?.message) {
      pushError(e.data.message)
    } else if (e.data?.errors) {
      // Handle validation errors structure
      Object.values(e.data.errors).forEach((errorArray) => {
        if (Array.isArray(errorArray)) {
          errorArray.forEach((errorMsg: string) => pushError(errorMsg))
        }
      })
    } else {
      // Fallback for unknown error structures
      pushError(t('shop.loginPage.failedFallback'))
    }
  }
}
</script>
<template>
<FormKitLazyProvider config-file="true">

  <div v-if="!isLoggedIn">
    <ShopNotifications />
    <FormKit type="form" :actions="false" :incomplete-message="false" @submit="invokeLogin">
      <div class="wrapper">
        <Email
          id="login-username"
          :required="true"
          name="username"
          :label="t('shop.loginPage.loginForm.emailPlaceholder')"
        />
        <Password
          id="login-password"
          :required="true"
          name="password"
          :label="t('shop.loginPage.loginForm.passwordPlaceholder')"
        />
        <Button primary slide type="submit">{{ t('shop.loginPage.loginForm.submit') }}</Button>
      </div>
    </FormKit>
    <div class="links" @click="$emit('close')">
      <NuxtLink :to="localePath(getPasswordRecoveryPermalink())">{{ t('shop.loginPage.forgotPassword') }}</NuxtLink>
      <h4>{{ t('shop.loginPage.registerTitle') }}</h4>
      <NuxtLink :to="localePath(getRegisterPermalink())">{{ t('shop.loginPage.register') }}</NuxtLink>
    </div>
  </div>

</FormKitLazyProvider>
</template>
<style scoped lang="scss">
.wrapper {
  display: grid;
  gap: var(--form-spacing);
  margin-bottom: var(--form-spacing);
}
.links {
  text-align: center;
  margin-bottom: var(--p-mb);
  a {
    color: var(--c-primary);
    text-decoration: underline;
  }
  h4 {
    margin: 20px 0 0 0;
    color: var(--dark);
  }
}
</style>
